// ===========================================
// FORECAST CHART — the business's own charts, live
// ===========================================
// Renders the exact three series the business plots in its own sales
// spreadsheet (last year actual · this year plan · this year actual),
// monthly and cumulative, for the whole business or one channel.
// Only appears when the account has `settings.overviewSeries` — accounts
// without imported chart data see nothing, not an empty frame.

import { useState } from 'react';
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

type SeriesSet = { y2025?: (number | null)[] | null; plan: number[]; actual: (number | null)[]; y2027plan?: number[] };
export type OverviewSeries = {
  source?: string;
  actualThrough: number; // months of real actuals (1-12)
  unit?: string;
  total: SeriesSet;
  channels: Record<string, SeriesSet>;
};

const cumulate = (arr: (number | null)[] | null | undefined): (number | null)[] => {
  if (!arr) return Array(12).fill(null);
  let s = 0;
  return arr.map((v) => (v == null ? null : Math.round((s += v) * 10) / 10));
};

const fmt = (v: number) => Math.round(v).toLocaleString('en-US');

export default function ForecastChart({ series }: { series: OverviewSeries }) {
  const [channel, setChannel] = useState('');
  const [view, setView] = useState<'monthly' | 'cumulative'>('monthly');
  // Year toggle appears once a next-year plan is loaded. 2027 view: plan bars
  // for next year, with this year's actuals as the reference line.
  const [year, setYear] = useState<2026 | 2027>(2026);
  const src = (channel && series.channels[channel]) || series.total;
  const channelNames = Object.keys(series.channels || {});
  const hasNextYear = !!series.total.y2027plan?.some((v) => v);
  const is27 = year === 2027 && hasNextYear;

  const rows = MONTHS.map((m, i) => {
    const pick = (a: (number | null)[] | null | undefined) => (a ? a[i] : null);
    const c = view === 'cumulative';
    if (is27) {
      return {
        m,
        ly: pick(c ? cumulate(src.actual) : src.actual),          // 2026 actual as reference
        plan: pick(c ? cumulate(src.y2027plan) : src.y2027plan),  // 2027 plan
        actual: null,                                             // no 2027 actuals yet
      };
    }
    return {
      m,
      ly: pick(c ? cumulate(src.y2025) : src.y2025),
      plan: pick(c ? cumulate(src.plan) : src.plan),
      actual: pick(c ? cumulate(src.actual) : src.actual),
    };
  });
  const hasLastYear = is27 ? !!src.actual?.some((v) => v) : (!!src.y2025 && src.y2025.some((v) => v));

  return (
    <div className="card p-5">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
        <div>
          <h2 className="font-semibold text-slate-900">Year at a glance</h2>
          <p className="text-xs text-slate-500">Cases by month — plan vs. what actually shipped{hasLastYear ? ', with last year for shape' : ''}</p>
        </div>
        <div className="flex items-center gap-2">
          {hasNextYear && (
            <div className="flex rounded-lg border border-slate-200 overflow-hidden text-xs">
              {([2026, 2027] as const).map((y) => (
                <button key={y} onClick={() => setYear(y)}
                  className={`px-2.5 py-1.5 ${year === y ? 'bg-slate-800 text-white' : 'bg-white text-slate-600'}`}>
                  {y === 2027 ? '2027 plan' : '2026'}
                </button>
              ))}
            </div>
          )}
          {channelNames.length > 1 && (
            <select value={channel} onChange={(e) => setChannel(e.target.value)}
              className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white text-slate-700">
              <option value="">Whole business</option>
              {channelNames.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          )}
          <div className="flex rounded-lg border border-slate-200 overflow-hidden text-xs">
            {(['monthly', 'cumulative'] as const).map((v) => (
              <button key={v} onClick={() => setView(v)}
                className={`px-2.5 py-1.5 capitalize ${view === v ? 'bg-slate-800 text-white' : 'bg-white text-slate-600'}`}>
                {v}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="h-64 -ml-2">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={rows} margin={{ top: 12, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis dataKey="m" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} width={44}
              tickFormatter={(v: number) => (v >= 1000 ? `${Math.round(v / 100) / 10}k` : String(v))} />
            <Tooltip formatter={(v: any, name: any) => [typeof v === 'number' ? `${fmt(v)} cases` : v, name]}
              contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0', fontSize: 12 }} />
            <Legend wrapperStyle={{ fontSize: 12 }} iconSize={10} />
            {view === 'monthly' ? (
              <>
                <Bar name={is27 ? '2027 plan' : '2026 plan'} dataKey="plan" fill={is27 ? '#bfdbfe' : '#e2e8f0'} radius={[4, 4, 0, 0]} />
                {!is27 && <Bar name="2026 actual" dataKey="actual" fill="#10b981" radius={[4, 4, 0, 0]} />}
              </>
            ) : (
              <>
                <Line name={is27 ? '2027 plan' : '2026 plan'} dataKey="plan" stroke={is27 ? '#3b82f6' : '#94a3b8'} strokeDasharray="5 4" strokeWidth={2} dot={false} type="monotone" />
                {!is27 && <Line name="2026 actual" dataKey="actual" stroke="#10b981" strokeWidth={2.5} dot={false} type="monotone" connectNulls={false} />}
              </>
            )}
            {hasLastYear && (
              <Line name={is27 ? '2026 actual' : '2025 actual'} dataKey="ly" stroke={is27 ? '#10b981' : '#c4b5fd'} strokeWidth={2} dot={false} type="monotone" connectNulls={false} />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <p className="text-[11px] text-slate-400 mt-1.5">
        {is27
          ? 'Next year\'s plan from the 2027 block of your forecast workbook, with this year\'s shipped cases for shape. No 2027 actuals yet, naturally.'
          : <>The same three series as the charts in your sales spreadsheet{series.source ? ` (${series.source})` : ''}. Actuals run through {MONTHS[Math.min(11, Math.max(0, (series.actualThrough || 0) - 1))]}; the plan line keeps going so you can see what the rest of the year asks for.</>}
      </p>
    </div>
  );
}
